<script>
import Repositories from '@/repositories';
const WorkOrderItemsRepository = Repositories.get('workorderitems');
import ManageReservationsModal from './modals/manage-reservations/';
import DeleteButton from '@/components/widgets/buttons/delete-button';
import statusMixin from '@/mixins/statusMixin';
import dateMixin from '@/mixins/dateMixin';
import StatusComponent from '@/components/widgets/badges/status';

export default {
  components: {
    ManageReservationsModal,
    DeleteButton,
    StatusComponent,
  },
  props: {
    workOrderItems: Array,
  },
  mixins: [statusMixin, dateMixin],
  data() {
    return {
      modalWoItem: null,
      isLoadingWorkOrderItemIdPublic: '',
      isErrorWorkOrderItemIdPublic: '',
    };
  },
  methods: {
    deleteItem(woItem) {
      // eslint-disable-next-line
      WorkOrderItemsRepository.delete(woItem.idPublic)
        .then(() => {
          this.$emit('wo-item-deleted', woItem);
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        });
    },
    manageItem(woItem) {
      this.modalWoItem = woItem;
      this.$bvModal.show('manage-reservations-modal');
    },
    woItemUpdated(woItem) {
      this.$emit('wo-item-updated', woItem);
    },
    modalClosed() {
      this.modalWoItem = null;
    },
    consumeWorkOrderItem(woItem, quantity) {
      this.isLoadingWorkOrderItemIdPublic = woItem.idPublic;
      this.isErrorWorkOrderItemIdPublic = '';
      WorkOrderItemsRepository.actions
        .consumeWorkOrderItem({ quantity }, woItem.idPublic)
        .then(() => {
          this.$emit('wo-item-updated', {
            ...woItem,
            consumedQuantity: woItem.consumedQuantity + quantity,
            reservedQuantity: woItem.reservedQuantity - quantity,
          });
        })
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
          this.isErrorWorkOrderItemIdPublic = woItem.idPublic;
        })
        .finally(() => {
          this.isLoadingWorkOrderItemIdPublic = '';
        });
    },
  },
  computed: {
    uniqueCategoriesTypesWithNames() {
      if (this.workOrderItems == null) return [];

      let reduced = this.workOrderItems.reduce((res, value) => {
        if (!res[value.categoryTypeIdPublic]) {
          res[value.categoryTypeIdPublic] = {
            categoryTypeIdPublic: value.categoryTypeIdPublic,
            price: 0,
          };
        }

        res[value.categoryTypeIdPublic].price +=
          value.price * value.totalQuantity;

        return res;
      }, []);

      reduced = Object.values(reduced);

      return reduced
        .map((i) => {
          let foundIndex = this.$store.state.cache.categoryTypes.findIndex(
            (x) => x.idPublic == i.categoryTypeIdPublic
          );

          let catName = 'none';

          if (foundIndex >= 0) {
            catName = this.$store.state.cache.categoryTypes[foundIndex].name;
          }

          return {
            idPublic: i.categoryTypeIdPublic,
            name: catName,
            price: i.price,
          };
        })
        .sort((a, b) => {
          if (a == null) {
            return 1;
          }
          if (b == null) {
            return -1;
          }

          if (a.idPublic < b.idPublic) {
            return -1;
          }

          return 1;
        });
    },
    workOrderItemsListWithStatues() {
      return this.workOrderItems.map((x) => {
        let timeDifference =
          new Date(x.leadTimeDate).getTime() - new Date().getTime();
        let daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
        x.leadTimeDays = x.leadTimeDate
          ? daysDifference
          : this.$t('errors.notAvailable');
        let status = this.getStatusFromCode(x.status, 'WorkOrderItems');
        return {
          ...x,
          ...status,
        };
      });
    },
    currencyFormatter() {
      return this.$store.state.cache.currencyFormatter;
    },
  },
};
</script>

<template>
  <div v-if="this.$store.state.cache.categoryTypes.length > 0">
    <div
      v-for="catType in uniqueCategoriesTypesWithNames"
      :key="catType.idPublic"
      class="mb-2"
    >
      <b-row>
        <b-col cols="6" class="mt-auto">
          <h3
            v-b-toggle="'collapse-' + catType.idPublic"
            style="text-transform: capitalize; margin-bottom: 0px"
          >
            {{
              $t('categories.types.' + catType.name.toLowerCase()) +
                ' ' +
                currencyFormatter.format(catType.price)
            }}
          </h3>
        </b-col>
        <b-col cols="6" class="text-right mt-auto">
          <b-button
            type="button"
            style="height: 40px"
            variant="primary"
            @click="$emit('automatic-reservation', catType.idPublic)"
          >
            {{
              $t('items.reserveX').replace(
                '$',
                $t(
                  'categories.types.' + catType.name.toLowerCase()
                ).toLowerCase()
              )
            }}
          </b-button>
        </b-col>
      </b-row>
      <b-collapse visible :id="'collapse-' + catType.idPublic" class="mt-2">
        <table class="table table-hover">
          <thead class="thead-light">
            <tr>
              <th class="table-row-relative">
                {{ $t('general.status') }}
              </th>
              <th class="table-row-expandable">
                {{ $t('items.item') }}
              </th>
              <th class="table-row-relative">
                {{ $t('items.requested') }}
              </th>
              <th class="table-row-relative">
                {{ $t('general.leadTime') }}
              </th>
              <th class="table-row-relative">
                {{ $t('items.ordered') }}
              </th>
              <th class="table-row-relative">
                {{ $t('items.reservedTotal') }}
              </th>
              <th class="table-row-relative">
                {{ $t('items.consumed') }}
              </th>
              <th class="table-row-relative">
                {{ $t('items.unitType') }}
              </th>
              <th class="table-row-relative">
                {{ $t('items.consume') }}
              </th>
              <th class="table-row-relative">
                {{ $t('general.manage') }}
              </th>
              <th class="table-row-relative">
                {{ $t('general.delete') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="woItem in workOrderItemsListWithStatues.filter(
                (x) => x.categoryTypeIdPublic == catType.idPublic
              )"
              :key="woItem.idPublic"
            >
              <td class="table-row-relative">
                <StatusComponent
                  :text="woItem.statusName"
                  :variant="woItem.statusVariant"
                />
              </td>
              <td class="table-row-expandable">
                {{ woItem.itemName }}
              </td>
              <td
                :class="{
                  'table-row-relative': true,
                  'text-danger': woItem.requestedQuantity > 0,
                  'font-weight-bold': woItem.requestedQuantity > 0,
                }"
              >
                {{ woItem.requestedQuantity }}
              </td>
              <td
                class="table-row-relative"
                v-if="
                  woItem.reservedQuantity +
                    woItem.orderedQuantity +
                    woItem.consumedQuantity >=
                    woItem.totalQuantity
                "
              >
                <i class="fas fa-check text-success pl-2 pr-2"></i>
              </td>
              <td
                class="table-row-relative"
                v-else-if="
                  woItem.reservedQuantity +
                    woItem.orderedQuantity +
                    woItem.consumedQuantity +
                    woItem.requestedQuantity >=
                    woItem.totalQuantity
                "
              >
                <i
                  class="fas fa-hourglass-half"
                  :class="
                    woItem.leadTimeDays != $t('errors.notAvailable') &&
                    woItem.leadTimeDays >= 0
                      ? 'text-warning'
                      : 'text-danger'
                  "
                ></i>
              </td>
              <td class="table-row-relative" v-else>
                <span
                  class="font-weight-bold"
                  :class="
                    woItem.leadTimeDays != $t('errors.notAvailable') &&
                    woItem.leadTimeDays >= 0
                      ? 'text-success'
                      : 'text-danger'
                  "
                >
                  {{ woItem.leadTimeDays }}
                </span>
              </td>
              <td
                :class="{
                  'table-row-relative': true,
                  'text-warning': woItem.orderedQuantity > 0,
                  'font-weight-bold': woItem.orderedQuantity > 0,
                }"
              >
                {{ woItem.orderedQuantity }}
              </td>
              <td
                :class="{
                  'table-row-relative': true,
                  'text-success':
                    woItem.reservedQuantity >= woItem.totalQuantity,
                  'font-weight-bold':
                    woItem.reservedQuantity >= woItem.totalQuantity,
                }"
              >
                {{ woItem.reservedQuantity }} /
                {{ toFloat(woItem.totalQuantity - woItem.consumedQuantity) }}
              </td>
              <td
                :class="{
                  'table-row-relative': true,
                  'text-success':
                    woItem.consumedQuantity >= woItem.totalQuantity,
                  'font-weight-bold':
                    woItem.consumedQuantity >= woItem.totalQuantity,
                }"
              >
                {{ woItem.consumedQuantity }} / {{ woItem.totalQuantity }}
              </td>
              <td class="table-row-relative">
                {{ woItem.itemUnitType }}
              </td>
              <td class="table-row-relative">
                <div v-if="isLoadingWorkOrderItemIdPublic == woItem.idPublic">
                  <i
                    class="fas fa-spinner fa-spin text-warning ml-2 mr-2"
                    style="font-size: 1.3em"
                  ></i>
                </div>
                <div
                  v-else-if="isErrorWorkOrderItemIdPublic == woItem.idPublic"
                  @click="consumeWorkOrderItem(woItem, woItem.reservedQuantity)"
                  style="cursor: pointer; font-size: 1.3em"
                >
                  <i class="fas fa-exclamation text-danger ml-2 mr-2"></i>
                </div>
                <div
                  v-else-if="woItem.reservedQuantity > 0"
                  @click="consumeWorkOrderItem(woItem, woItem.reservedQuantity)"
                  style="cursor: pointer; font-size: 1.3em"
                >
                  <i class="fas fa-check text-success pl-2 pr-2"></i>
                </div>
              </td>
              <td class="table-row-relative">
                <i
                  class="fas fa-cogs text-muted pl-2 pr-2"
                  @click="manageItem(woItem)"
                  style="cursor: pointer; font-size: 1.3em"
                ></i>
              </td>
              <td class="table-row-relative">
                <DeleteButton @clicked="deleteItem(woItem)" />
              </td>
            </tr>
          </tbody>
        </table>
      </b-collapse>
    </div>
    <ManageReservationsModal
      :modalInfo="modalWoItem"
      @wo-item-updated="woItemUpdated"
      @modal-closed="modalClosed"
    />
  </div>
</template>

<style scoped>
.red-text {
  color: red;
}
.yellow-text {
  color: rgb(212, 212, 48);
}
</style>
